/**
 * @description 删除确认功能Hook
 * @date 2025-01-17
 * @author lv.chen
 */

import { useState } from 'react';
import { notification } from 'tea-component';

import { delTrueDataFeedbackHistory } from '@intelligence/services/topcharts';

import type { IPublicFeedbackParams } from '../types';

interface IUseDeleteConfirmParams {
  /** 国际化格式化方法 */
  formatMessage: IFormatMessage;
  /** 反馈查询参数 */
  feedbackParams: IPublicFeedbackParams;
  /** 获取反馈历史数据的方法 */
  fetchTrueDataFeedbackHistory: (params: IPublicFeedbackParams) => Promise<void>;
}

interface IUseDeleteConfirmReturn {
  /** 是否显示删除确认弹窗 */
  isDeleteConfirmVisible: boolean;
  /** 待删除的记录ID */
  deleteId: number;
  /** 打开删除确认弹窗 */
  openDeleteConfirm: (id: number) => void;
  /** 关闭删除确认弹窗 */
  closeDeleteConfirm: () => void;
  /** 执行删除操作 */
  handleDelete: () => Promise<void>;
}

/**
 * 删除确认功能Hook
 */
export default function useDeleteConfirm(params: IUseDeleteConfirmParams): IUseDeleteConfirmReturn {
  const { formatMessage, feedbackParams, fetchTrueDataFeedbackHistory } = params;

  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(0);

  /**
   * 打开删除确认弹窗
   */
  const openDeleteConfirm = (id: number): void => {
    setDeleteId(id);
    setIsDeleteConfirmVisible(true);
  };

  /**
   * 关闭删除确认弹窗
   */
  const closeDeleteConfirm = (): void => {
    setIsDeleteConfirmVisible(false);
  };

  /**
   * 执行删除操作
   */
  const handleDelete = async (): Promise<void> => {
    if (!deleteId) {
      setIsDeleteConfirmVisible(false);
      return;
    }

    try {
      await delTrueDataFeedbackHistory({ id: deleteId });
      fetchTrueDataFeedbackHistory(feedbackParams);

      notification.success({
        unique: true,
        duration: 1000,
        description: formatMessage({
          id: 'dbi_success_opt',
          defaultMessage: 'Success Delete!',
        }),
      });
    } catch (error) {
      notification.error({
        unique: true,
        duration: 1000,
        description: formatMessage({
          id: 'dbi_feedback_delete_failed',
          defaultMessage: 'Failed Delete!',
        }),
      });
    } finally {
      setIsDeleteConfirmVisible(false);
    }
  };

  return {
    isDeleteConfirmVisible,
    deleteId,
    openDeleteConfirm,
    closeDeleteConfirm,
    handleDelete,
  };
}
